/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type { ComponentType } from "react"
import { useEffect } from "react"
import CodeBlock from "@tiptap/extension-code-block"
import { ReactNodeViewRenderer } from "@tiptap/react"
import { common, createLowlight } from "lowlight"
import { Plugin } from "prosemirror-state"

import type { Editor } from "@tiptap/core"
import type { CodeBlockOptions } from "@tiptap/extension-code-block"
import type { NodeViewProps } from "@tiptap/react"
import { LowlightPlugin } from "../plugin/lowlight-plugin"
import { CodeBlockComponent } from "./components/custom-code-block"

// The full grammar set is ~1MB gzipped, so only the editor loads it, and lazily.
// Readers (portal, widget) highlight the common set, which is what they always did.
export const codeBlockLowlight = createLowlight(common)

// haskell's grammar has a regex with \0 that throws in production builds
const SKIPPED_LANGUAGES = new Set(["haskell"])

let allLanguagesLoaded: Promise<void> | null = null

export function registerAllCodeBlockLanguages(): Promise<void> {
	allLanguagesLoaded ??= import("highlight.js").then(({ default: hljs }) => {
		for (const name of hljs.listLanguages()) {
			if (SKIPPED_LANGUAGES.has(name) || codeBlockLowlight.registered(name)) {
				continue
			}
			const grammar = hljs.getLanguage(name)?.rawDefinition
			if (grammar) codeBlockLowlight.register(name, grammar)
		}
	})
	return allLanguagesLoaded
}

export function useAllCodeBlockLanguages(editor: Editor | null | undefined) {
	useEffect(() => {
		let cancelled = false
		void registerAllCodeBlockLanguages().then(() => {
			// Decorations only recompute on a transaction, so nudge the view once.
			if (!cancelled && editor && !editor.isDestroyed) {
				editor.view.dispatch(editor.state.tr)
			}
		})
		return () => {
			cancelled = true
		}
	}, [editor])
}

const lowlight = codeBlockLowlight

export interface CodeBlockLowlightOptions extends CodeBlockOptions {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	lowlight: any
}

export const CodeBlockLowlight = CodeBlock.extend<CodeBlockLowlightOptions>({
	addOptions() {
		return {
			...this.parent?.(),
			lowlight: {},
			languageClassPrefix: "language-",
			exitOnTripleEnter: true,
			exitOnArrowDown: true,
			defaultLanguage: null,
			HTMLAttributes: {},
		}
	},

	addProseMirrorPlugins() {
		return [
			...(this.parent?.() || []),
			LowlightPlugin({
				name: this.name,
				lowlight: this.options.lowlight,
				defaultLanguage: this.options.defaultLanguage,
			}),
		]
	},
})

interface CustomCodeBlockOptions extends CodeBlockLowlightOptions {
	preClassName?: string
	isLanguageEditable?: boolean
	isHTMLLabel?: boolean
}

const LABEL_CLASSES =
	"absolute right-10 top-2 flex h-[1.5rem] font-[inherit] flex-row items-center justify-center gap-1 rounded-lg bg-white px-2 py-1 text-sm font-medium text-gray-700 shadow-sm dark:bg-gray-800 dark:text-white"

const CustomCodeBlock = CodeBlockLowlight.extend<CustomCodeBlockOptions>({
	addOptions() {
		return {
			...this.parent?.(),
			preClassName: "",
			isLanguageEditable: true, // Default value for isLanguageEditable
			isHTMLLabel: false, // Default value for isHTMLLabel
		}
	},

	addKeyboardShortcuts() {
		return {
			...this.parent?.(),
			"Mod-Shift-/": () => this.editor.commands.toggleCodeBlock(),
			Tab: ({ editor }) => {
				const { selection } = editor.state
				const { $from } = selection
				if ($from.parent.type !== this.type) {
					return false
				}
				editor.commands.insertContent("\t")
				return true
			},
		}
	},

	addProseMirrorPlugins() {
		const plugins = this.parent?.() ?? []
		const { editor, name } = this

		const pastePlugin = new Plugin({
			props: {
				handlePaste: (view, event: ClipboardEvent) => {
					const { selection } = editor.state
					const { $from } = selection
					if ($from.parent.type.name !== name) {
						return false
					}

					const text = event.clipboardData?.getData("text/plain")
					if (!text) return false

					// Strip markdown code fences if the entire pasted content is wrapped in them
					const fenceMatch = text.match(
						/^```(?:[a-z]*)?\n?([\s\S]*?)\n?```\s*$/,
					)
					const content = fenceMatch ? (fenceMatch[1] ?? "") : text

					event.preventDefault()
					view.dispatch(
						view.state.tr.insertText(content, selection.from, selection.to),
					)
					return true
				},
			},
		})

		return [...plugins, pastePlugin]
	},

	addAttributes() {
		return {
			// The tab label of a code group; markdown carries it as the fence's
			// filename, so it has to survive an HTML round trip.
			title: {
				default: null,
				parseHTML: (element) => element.getAttribute("data-title"),
				renderHTML: (attributes: Record<string, unknown>) =>
					attributes.title ? { "data-title": String(attributes.title) } : {},
			},
			language: {
				default: this.options.defaultLanguage,
				parseHTML: (element) => {
					const { languageClassPrefix } = this.options

					// First, check for data-language attribute (most reliable)
					const dataLanguage = element.getAttribute("data-language")
					if (dataLanguage) {
						return dataLanguage
					}

					// Try to find language from code element's class
					const codeElement = element.querySelector("code")
					const targetElement = codeElement || element

					// Get class list - handle both DOMTokenList and string className
					let classNames: string[] = []
					if (targetElement.classList && targetElement.classList.length > 0) {
						classNames = Array.from(targetElement.classList)
					} else if (
						typeof targetElement.className === "string" &&
						targetElement.className
					) {
						classNames = targetElement.className.split(/\s+/).filter(Boolean)
					}

					// Also check the pre element's classes if we checked code element
					if (codeElement && classNames.length === 0) {
						if (element.classList && element.classList.length > 0) {
							classNames = Array.from(element.classList)
						} else if (
							typeof element.className === "string" &&
							element.className
						) {
							classNames = element.className.split(/\s+/).filter(Boolean)
						}
					}

					const languages = classNames
						.filter((className) => className.startsWith(languageClassPrefix))
						.map((className) => className.replace(languageClassPrefix, ""))

					const language = languages[0]

					if (!language) {
						return null
					}

					return language
				},
				rendered: false,
			},
		}
	},
	addNodeView() {
		return ReactNodeViewRenderer(
			CodeBlockComponent as ComponentType<NodeViewProps>,
		)
	},
	renderHTML(input) {
		const { node, HTMLAttributes } = input
		const { language } = node.attrs

		const { defaultLanguage, preClassName, languageClassPrefix, isHTMLLabel } =
			this.options

		const languageClass = node.attrs.language
			? `${languageClassPrefix}${node.attrs.language}`
			: ""

		const childrenElements: unknown[] = []

		if (isHTMLLabel) {
			childrenElements.push([
				"div",
				{ class: LABEL_CLASSES },
				language || defaultLanguage,
			])
		}

		// Add copy button
		childrenElements.push([
			"button",
			{
				class:
					"absolute right-2 top-2 z-10 flex cursor-pointer items-center gap-1 rounded-lg bg-white px-1.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm ring-1 ring-black/5 dark:bg-gray-800 dark:text-gray-200 dark:ring-white/10",
				"data-code-copy": "",
			},
			[
				"svg",
				{
					xmlns: "http://www.w3.org/2000/svg",
					width: "24",
					height: "24",
					viewBox: "0 0 24 24",
					fill: "none",
					stroke: "currentColor",
					"stroke-width": "2",
					"stroke-linecap": "round",
					"stroke-linejoin": "round",
					class:
						"lucide lucide-copy-icon lucide-copy h-3.5 w-3.5 transition-transform duration-200 ease-in-out hover:scale-110",
				},
				[
					"rect",
					{ width: "14", height: "14", x: "8", y: "8", rx: "2", ry: "2" },
				],
				[
					"path",
					{
						d: "M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2",
					},
				],
			],
		])

		// Use content hole (0) so ProseMirror serializes text natively,
		// preserving newlines correctly in the stored HTML
		childrenElements.push(["code", { class: languageClass + " relative" }, 0])

		return [
			"pre",
			{
				...HTMLAttributes,
				class:
					`relative ${HTMLAttributes.class || ""} ${languageClass} ${preClassName || ""}`.trim(),
				"data-language": language || defaultLanguage || null,
			},
			...childrenElements,
		]
	},

	parseHTML() {
		return [
			{
				tag: "pre",
				preserveWhitespace: "full",
			},
		]
	},
}).configure({
	lowlight,
	defaultLanguage: "plaintext",
	languageClassPrefix: "language-",
})

export default CustomCodeBlock
